import { normalizeControl, type ChessClockControl } from '@/views/main/clock/utils/chess-clock';
import { createHandshakeGameSuggestion, type HandshakeGamePlayer } from '@/views/main/handshakes/utils/handshake-game';

/**
 * Long enough for an @ and a username, short enough that a crafted link cannot
 * fill a half. Shared with the names the clock itself shows.
 */
export const MAX_LABEL_LENGTH = 40;

/**
 * Long enough for a user id, short enough that a crafted link cannot be used as
 * the seed of a different game by being silently cut down.
 */
const MAX_ID_LENGTH = 64;

type QueryValue = string | null | Array<string | null> | undefined;
export type ClockHandshakeQuery = Record<string, QueryValue>;

export interface ClockHandshake {
  handshakeId: string;
  players: [HandshakeGamePlayer, HandshakeGamePlayer];
  /** Which recommendation this clock was opened on. Zero is the first. */
  round: number;
  /**
   * What the recommendation is drawn from, when that is not the handshake id.
   * Absent on a handshake that was not dealt on a clock first.
   */
  suggestionSeed?: string;
}

/** What restarting the clock from a recommendation sets it to. */
export interface ClockRecommendation {
  minutes: number;
  increment: number;
  side: 'w' | 'b';
  round: number;
  /** The position this play saved. Absent when the box was left unticked. */
  position?: number | null;
}

const read = (query: ClockHandshakeQuery, key: string, maxLength: number): string | undefined => {
  const value = query[key];
  const first = Array.isArray(value) ? value[0] : value;

  if (!first || first.length > maxLength) return undefined;

  return first;
};

/** A name, cut to what the clock will actually show rather than refused. */
const readLabel = (query: ClockHandshakeQuery, key: string): string | undefined => {
  const value = query[key];
  const first = Array.isArray(value) ? value[0] : value;

  return first ? first.slice(0, MAX_LABEL_LENGTH) : undefined;
};

/** The name as it is written on the clock, without the @ a member's carries. */
const usernameFromLabel = (label: string | undefined): string | undefined => {
  if (!label) return undefined;

  const username = label.startsWith('@') ? label.slice(1) : label;

  return username || undefined;
};

/**
 * What the clock's URL has to carry, on top of the names, so the same
 * recommendation can be opened again from the clock. The names already say who
 * sits where; this says which handshake, and which role each of them has,
 * which is what the colours and the position are drawn from.
 */
const readRound = (query: ClockHandshakeQuery): number => {
  const raw = read(query, 'round', 8);

  if (!raw || !/^\d+$/.test(raw)) return 0;

  return Number(raw);
};

export function clockHandshakeParams(
  handshakeId: string,
  near: HandshakeGamePlayer,
  far: HandshakeGamePlayer,
  round = 0,
  suggestionSeed?: string,
): Record<string, string> {
  return {
    hs: handshakeId,
    me: near.id,
    role: near.role === 'initiator' ? 'i' : 'r',
    round: String(round),
    ...(far.isPasserby ? { pass: '1' } : { peer: far.id }),
    ...(suggestionSeed ? { seed: suggestionSeed } : {}),
  };
}

/**
 * The handshake a clock was opened from, or nothing when the URL is a clock on
 * its own. Both players or neither: one side without the other is not a game.
 */
export function readClockHandshake(query: ClockHandshakeQuery): ClockHandshake | null {
  const handshakeId = read(query, 'hs', MAX_ID_LENGTH);
  const nearId = read(query, 'me', MAX_ID_LENGTH);
  const role = read(query, 'role', MAX_ID_LENGTH);
  const nearName = usernameFromLabel(readLabel(query, 'near'));
  const farName = usernameFromLabel(readLabel(query, 'far'));

  if (!handshakeId || !nearId || !nearName || !farName || (role !== 'i' && role !== 'r')) return null;

  const nearRole = role === 'i' ? 'initiator' : 'responder';
  const farRole = role === 'i' ? 'responder' : 'initiator';
  const passerby = read(query, 'pass', MAX_ID_LENGTH) === '1';
  const peerId = read(query, 'peer', MAX_ID_LENGTH);

  if (!passerby && !peerId) return null;

  const near: HandshakeGamePlayer = { id: nearId, username: nearName, role: nearRole };
  const far: HandshakeGamePlayer = passerby
    ? { id: `${handshakeId}:passerby`, username: farName, role: farRole, isPasserby: true }
    : { id: peerId!, username: farName, role: farRole };

  const suggestionSeed = read(query, 'seed', 32);

  return {
    handshakeId,
    players: [near, far],
    round: readRound(query),
    ...(suggestionSeed ? { suggestionSeed } : {}),
  };
}

/**
 * A game dealt on a clock opened on its own. There is no handshake to seed it
 * from, so the clock makes up an id for it, and nobody else hears about it.
 */
export interface ClockLocalGame {
  gameId: string;
  round: number;
}

const LOCAL_GAME_ID = /^[a-z0-9]{1,16}$/;

export function createClockGameId(): string {
  return Math.random().toString(36).slice(2, 12) || '0';
}

/** What the suggestion is seeded from, kept apart from anything a handshake id could be. */
export function clockLocalGameSeed(gameId: string): string {
  return `clock:${gameId}`;
}

/** The game id inside a clock seed, or nothing when the seed is a handshake. */
export function clockGameIdFromSeed(seed: string): string | null {
  const prefix = 'clock:';
  if (!seed.startsWith(prefix)) return null;

  const gameId = seed.slice(prefix.length);

  return LOCAL_GAME_ID.test(gameId) ? gameId : null;
}

/**
 * The recommendation a clock showed before anyone sat down. The board, the
 * colour, and the time, so closing the clock and opening it again is that
 * same game. A coplayer writes it onto the handshake instead.
 */
export interface SoloClockRecommendation {
  gameId: string;
  round: number;
  clockChosen: boolean;
  minutes: number | null;
  increment: number | null;
  /** Null keeps the seed's colour. */
  initiatorPlaysWhite: boolean | null;
  /**
   * The number the server drew for this player on `round`. Kept so the clock
   * opens on it, rather than on the seed's rank and then the draw.
   */
  position?: number;
}

const SOLO_RECOMMENDATION_KEY = 'gam_clock_recommendation';

export function readSoloClockRecommendation(
  storage: Pick<Storage, 'getItem'> = localStorage,
): SoloClockRecommendation | null {
  try {
    const raw = storage.getItem(SOLO_RECOMMENDATION_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<SoloClockRecommendation>;
    if (typeof parsed.gameId !== 'string' || !LOCAL_GAME_ID.test(parsed.gameId)) return null;
    if (!Number.isInteger(parsed.round) || parsed.round == null || parsed.round < 0 || parsed.round > 99) return null;

    const clockChosen = parsed.clockChosen === true;
    const control =
      clockChosen && parsed.minutes != null && parsed.increment != null
        ? normalizeControl(parsed.minutes, parsed.increment)
        : null;
    if (clockChosen && !control) return null;

    if (parsed.initiatorPlaysWhite != null && typeof parsed.initiatorPlaysWhite !== 'boolean') return null;

    const position =
      Number.isInteger(parsed.position) && parsed.position! >= 0 && parsed.position! < 960
        ? parsed.position
        : undefined;

    return {
      gameId: parsed.gameId,
      round: parsed.round,
      clockChosen,
      minutes: control?.minutes ?? null,
      increment: control?.increment ?? null,
      initiatorPlaysWhite: parsed.initiatorPlaysWhite ?? null,
      ...(position != null ? { position } : {}),
    };
  } catch {
    return null;
  }
}

export function rememberSoloClockRecommendation(
  recommendation: SoloClockRecommendation,
  storage: Pick<Storage, 'setItem'> = localStorage,
): void {
  try {
    storage.setItem(SOLO_RECOMMENDATION_KEY, JSON.stringify(recommendation));
  } catch {
    // A private window can refuse the write. The board still shows for this visit.
  }
}

/** A new draw for the game already kept. Nothing, when that game has moved on or gone. */
export function rememberSoloClockPosition(
  gameId: string,
  round: number,
  position: number,
  storage: Pick<Storage, 'getItem' | 'setItem'> = localStorage,
): void {
  const kept = readSoloClockRecommendation(storage);
  if (!kept || kept.gameId !== gameId || kept.round !== round) return;

  rememberSoloClockRecommendation({ ...kept, position }, storage);
}

/**
 * Playing or saving a position anywhere can take the kept game's number off
 * the table. Ask again, so the next bare clock opens on the new one.
 */
export async function refreshSoloClockDraw(
  deal: (seed: string, round: number) => Promise<{ data?: { positionIndex: number } }>,
  storage: Pick<Storage, 'getItem' | 'setItem'> = localStorage,
): Promise<void> {
  const kept = readSoloClockRecommendation(storage);
  if (!kept) return;

  const result = await deal(clockLocalGameSeed(kept.gameId), kept.round);
  if (result.data?.positionIndex == null) return;

  rememberSoloClockPosition(kept.gameId, kept.round, result.data.positionIndex, storage);
}

export function forgetSoloClockRecommendation(storage: Pick<Storage, 'removeItem'> = localStorage): void {
  try {
    storage.removeItem(SOLO_RECOMMENDATION_KEY);
  } catch {
    // The handshake already has the game. A leftover local copy only lasts this visit.
  }
}

/**
 * The board to open on a clock that names no game of its own. A game already
 * on the address, or a handshake, stays that game.
 */
export function restoredSoloClockGame(
  query: ClockHandshakeQuery,
  storage: Pick<Storage, 'getItem'> = localStorage,
): SoloClockRecommendation | null {
  if (readClockLocalGame(query) || readClockHandshake(query)) return null;

  return readSoloClockRecommendation(storage);
}

/**
 * The colour already chosen for this game, so the clock opens on it. White is
 * only the stand-in until that choice is known; using it whenever the holder
 * plays black shows white and then switches.
 */
export function clockHolderColour(input: {
  seed: string;
  players: [HandshakeGamePlayer, HandshakeGamePlayer];
  holderId: string;
  round: number;
  initiatorPlaysWhite: boolean | null;
}): 'white' | 'black' {
  const suggestion = createHandshakeGameSuggestion(input.seed, input.players, input.round, null, {
    initiatorPlaysWhite: input.initiatorPlaysWhite,
  });

  return suggestion.white.id === input.holderId ? 'white' : 'black';
}

const PLAIN_CLOCK_CONTROL_KEY = 'gam_clock_control';

/**
 * The time a plain clock was last set to. A game and a recommendation carry
 * their own time on the address, so this is only what a clock with neither
 * comes back to.
 */
export function readPlainClockControl(storage: Pick<Storage, 'getItem'> = localStorage): ChessClockControl | null {
  try {
    const raw = storage.getItem(PLAIN_CLOCK_CONTROL_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as { minutes?: unknown; increment?: unknown };
    if (parsed.minutes == null || parsed.increment == null) return null;

    return normalizeControl(parsed.minutes, parsed.increment);
  } catch {
    return null;
  }
}

export function rememberPlainClockControl(
  control: ChessClockControl,
  storage: Pick<Storage, 'setItem'> = localStorage,
): void {
  try {
    storage.setItem(
      PLAIN_CLOCK_CONTROL_KEY,
      JSON.stringify({ minutes: control.minutes, increment: control.increment }),
    );
  } catch {
    // A private window can refuse the write. The clock still shows the time for this visit.
  }
}

/**
 * The time a clock shows before anyone has chosen one: the same 10+3 the
 * recommendation starts on. The drawer presets stay their own list, and
 * highlight only when this pair is one of them.
 */
export function clockRecommendationControl(gameId: string, round = 0): ChessClockControl {
  const players = clockLocalPlayers(gameId, null, { guest: '', passerby: '' });
  const suggestion = createHandshakeGameSuggestion(clockLocalGameSeed(gameId), players, round);

  return { minutes: suggestion.minutes, increment: suggestion.increment };
}

/**
 * Whether this ping is a handshake the clock was waiting on, now ready.
 * A photo, a note, and another position use the same ping. They are not a new
 * handshake, so they do not celebrate. Waiting is remembered until that
 * handshake is ready once.
 */
export function isNewlyReadyHandshake(handshakeId: string, ready: boolean, waiting: Set<string>): boolean {
  if (!ready) {
    waiting.add(handshakeId);
    return false;
  }

  return waiting.delete(handshakeId);
}

/** The game this clock dealt itself, or nothing when it has not dealt one. */
export function readClockLocalGame(query: ClockHandshakeQuery): ClockLocalGame | null {
  const gameId = read(query, 'game', 16);

  if (!gameId || !LOCAL_GAME_ID.test(gameId)) return null;

  return { gameId, round: readRound(query) };
}

/**
 * The two players of a game dealt on the clock: whoever holds the phone, and a
 * passerby across the table. The holder always takes the first role, so the
 * same game is dealt whether or not they are signed in.
 */
export function clockLocalPlayers(
  gameId: string,
  holder: { id: string; username: string } | null,
  labels: { guest: string; passerby: string },
): [HandshakeGamePlayer, HandshakeGamePlayer] {
  const near: HandshakeGamePlayer = holder
    ? { id: holder.id, username: holder.username, role: 'initiator' }
    : { id: `${clockLocalGameSeed(gameId)}:holder`, username: labels.guest, role: 'initiator', isGuest: true };

  return [
    near,
    {
      id: `${clockLocalGameSeed(gameId)}:passerby`,
      username: labels.passerby,
      role: 'responder',
      isPasserby: true,
    },
  ];
}
